import { useT } from '@/store';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState, SkeletonRows, EmptyState } from '@/components/ui/Panel';
import { usePromiseKeeping } from '@/hooks/usePromiseKeeping';

/**
 * Ar pristatėm tada, kada sakėm — pagal objektą.
 *
 * ClientReliabilityModal veidrodis, ir tyčia toks pat: tas atsako, kiek
 * galima pasitikėti kliento objekto duomenimis, šis — kiek klientas gali
 * pasitikėti mumis. Ta pati lentelė, tos pačios sąžiningumo taisyklės ir tas
 * pats mažos imties elgesys, nes du skirtingi to paties klausimo formos
 * atvaizdavimai vienoje konsolėje verstų dispečerį skaityti abu iš naujo.
 *
 * VARDIKLIS YRA PAŽADAI, ne reisai — ir todėl stulpelis vadinasi „pažadėta",
 * o ne „pristatyta". Krovinys, kurio niekas nežadėjo konkrečiai dienai, čia
 * nedalyvauja, ir eilutės skaičius nėra „kiek jam vežėm": tai būtų du
 * skirtingi skaičiai tuo pačiu vardu dviejuose ekranuose.
 */
export function PromiseKeepingModal({ open, onClose }) {
  const t = useT();
  const { data, isLoading, isError, error } = usePromiseKeeping();
  const rows = data?.rows ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('promiseKeeping.title')}
      width="max-w-[720px]"
      footer={
        <button type="button" className="btn" onClick={onClose}>
          {t('common.closeBtn')}
        </button>
      }
    >
      <div className="space-y-3">
        {isError ? <ErrorState error={error} /> : null}
        {isLoading ? <SkeletonRows rows={6} /> : null}

        {!isLoading && data ? (
          rows.length === 0 ? (
            <EmptyState>{t('promiseKeeping.noPromises')}</EmptyState>
          ) : (
            <>
              <p className="text-[11px] text-dim">
                {t('promiseKeeping.windowNote', { days: data.window_days })}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[11px]">
                  <thead>
                    <tr className="text-dim">
                      <th className="px-2 py-1 font-semibold">{t('promiseKeeping.col.client')}</th>
                      <th className="px-2 py-1 font-semibold">{t('promiseKeeping.col.site')}</th>
                      <th className="px-2 py-1 font-semibold">{t('promiseKeeping.col.promised')}</th>
                      <th className="px-2 py-1 font-semibold">{t('promiseKeeping.col.onTime')}</th>
                      <th className="px-2 py-1 font-semibold">{t('promiseKeeping.col.worst')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.site_id}
                        className={`border-t border-line ${row.low_sample ? 'opacity-55' : ''}`}
                      >
                        <td className="px-2 py-1 font-semibold text-ink">{row.client_name}</td>
                        <td className="px-2 py-1">{row.site_name}</td>
                        <td className="px-2 py-1">{row.promised_count}</td>
                        <td className="px-2 py-1">
                          <RateBadge rate={row.on_time_rate} lowSample={row.low_sample} t={t} />
                        </td>
                        <td className="px-2 py-1">
                          {/* Nulis rodomas kaip brūkšnys, ne kaip „0 dienų":
                              objektas, kuriam niekada nevėlavom, neturi
                              vėlavimo, o nulis skaitomas kaip išmatuotas
                              mažiausias vėlavimas. */}
                          {row.worst_days_late
                            ? t('promiseKeeping.daysLate', { days: row.worst_days_late })
                            : <span className="text-dim">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10.5px] text-dim">
                {t('promiseKeeping.lowSampleNote', { n: data.low_sample_threshold })}
              </p>
            </>
          )
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Maža imtis rodoma neutraliai, kad ir koks būtų jos skaičius — vienas
 * pavėluotas krovinys, nudažytas raudonai, pateiktų „neišmatuota" kaip
 * „patikrinta ir blogai". Tiksliai tas pats sprendimas, kaip
 * ClientReliabilityModal'e, ir jis kartojamas todėl, kad abu ekranai
 * atsakinėja tos pačios formos klausimą.
 */
function RateBadge({ rate, lowSample, t }) {
  if (rate === null || rate === undefined) {
    return <span className="text-dim">{t('promiseKeeping.noData')}</span>;
  }
  const pct = Math.round(rate * 100);
  const tone = lowSample ? 'text-dim' : pct >= 90 ? 'text-green' : pct >= 70 ? 'text-amber' : 'text-red';
  return (
    <span className={`font-semibold ${tone}`}>
      {pct}%{lowSample ? ` (${t('promiseKeeping.lowSampleBadge')})` : ''}
    </span>
  );
}
